import { atom } from 'recoil';
import omit from 'lodash/omit';

import { Preferences } from 'common/types';
import * as K from 'common/constants';
import { getDashboardLink } from 'common/util/location';
import { isTimezoneContingent } from 'common/util/time';
import { recoilSelector } from 'common/util/recoil-selector';
import { activeTeamState, userDataState } from 'components/account/account.state';
import { searchParamState } from 'components/common/Managers/RouteStateManager.state';
import { isPathValidScalyrRoute } from 'components/common/Router/Router.routes';

export const preferencesState = atom<Preferences>({
  key: K.RECOIL_ATOM_KEY.PREFERENCES,
  default: K.DEFAULT_PREFERENCES,
});

export const userTeamHomepageState = recoilSelector({
  key: K.RECOIL_SELECTOR_KEY.HOME_PAGE_USER_TEAM,
  get: ({ get }) => {
    const { homepages } = get(preferencesState);
    const activeTeam = get(activeTeamState);
    return homepages.find(({ team }) => team === activeTeam.id)?.path;
  },
});

/**
 * Returns the list of available sections the current user has available
 */
export const homepageAvailableSectionsState = recoilSelector({
  key: K.RECOIL_SELECTOR_KEY.HOME_PAGE_AVAILABLE_SECTIONS,
  get: ({ get }) => {
    const userData = get(userDataState);
    return [
      [
        {
          displayText: K.ROUTE_NAME.LOGS,
          value: K.ROUTE.LOG_START,
        },
        {
          displayText: K.ROUTE_NAME.SEARCH,
          value: K.ROUTE.EVENTS,
        },
        {
          displayText: K.ROUTE_NAME.ALERTS,
          value: K.ROUTE.ALERTS,
        },
        {
          displayText: K.ROUTE_NAME.DASHBOARD_LIST,
          value: K.ROUTE.DASHBOARD_LIST,
        },
      ],
      userData.dashboardsV2.map(({ name }) => ({
        displayText: `${name} || Dashboard`,
        value: getDashboardLink(name),
      })),
      userData.privateSearches.map(({ name, url }) => ({
        displayText: `${name} || Private Search`,
        value: url,
      })),
      userData.sharedSearches.map(({ name, url }) => ({
        displayText: `${name} || Shared Search`,
        value: url,
      })),
    ];
  },
});

export const homepagesExcludingCurrentState = recoilSelector({
  key: K.RECOIL_SELECTOR_KEY.HOME_PAGE_EXCLUDING_CURRENT,
  get: ({ get }) => {
    const { homepages } = get(preferencesState);
    const activeTeam = get(activeTeamState);
    return homepages.filter(({ team }) => team !== activeTeam.id).map((h) => omit(h, '__typename'));
  },
});

/**
 * Checks if homepage configured in preferences is a valid route or not
 */
export const homepageIsValidState = recoilSelector({
  key: K.RECOIL_SELECTOR_KEY.HOME_PAGE_IS_VALID,
  get: ({ get }) => {
    const homepage = get(userTeamHomepageState);
    if (homepage) {
      return isPathValidScalyrRoute(homepage || '');
    }
  },
});

export const timezoneState = recoilSelector({
  key: K.RECOIL_SELECTOR_KEY.TIMEZONE,
  get: ({ get }) => get(preferencesState).timezone,
});

/**
 * If true, a new search is required if the user changes their timezone preference.
 */
export const timezoneChangeRequiresNewSearchState = recoilSelector({
  key: K.RECOIL_SELECTOR_KEY.TIMEZONE_CHANGE_REQUIRES_NEW_SEARCH,
  get: ({ get }) => {
    const startTime = get(searchParamState(K.URL_PARAM.START_TIME));
    const endTime = get(searchParamState(K.URL_PARAM.END_TIME));
    return isTimezoneContingent(startTime) || isTimezoneContingent(endTime);
  },
});
